import { RowSelectionState, SortingState } from '@tanstack/react-table';
import { featureCollection } from '@turf/turf';
import { FeatureCollection, Point } from 'geojson';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { MapRef } from 'react-map-gl';

import { useGetDraftPlot } from '@/api/rest/resources/plot';
import { DraftPlot } from '@/api/rest/resources/types/plot';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

function buildDraftPlotsGeoJSON(plots: DraftPlot[]) {
  const withGeometry = plots.filter((p) => p.multi_polygon);
  if (!withGeometry.length) return null;
  return featureCollection(
    withGeometry.map((p) => ({
      type: 'Feature' as const,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      geometry: p.multi_polygon!,
      properties: { id: p.id, name: p.name },
    })),
  );
}

/**
 * Computes centroid features from plot geometry. This is the expensive part (flattening every
 * polygon's coordinates to find the bounding box), so it's kept independent of row selection -
 * it should only be recomputed when the plot list itself changes, not on every checkbox toggle.
 */
function buildCentroidFeatures(plots: DraftPlot[]) {
  return plots.flatMap((p) => {
    if (!p.multi_polygon) return [];
    const geometry = p.multi_polygon as unknown as { coordinates: number[][][][] };
    const coords = geometry.coordinates.flat(2);
    const lngs = coords.map((c) => Number(c[0])).filter(Number.isFinite);
    const lats = coords.map((c) => Number(c[1])).filter(Number.isFinite);
    if (!lngs.length || !lats.length) return [];
    const minLng = lngs.reduce((a, b) => Math.min(a, b), Infinity);
    const maxLng = lngs.reduce((a, b) => Math.max(a, b), -Infinity);
    const minLat = lats.reduce((a, b) => Math.min(a, b), Infinity);
    const maxLat = lats.reduce((a, b) => Math.max(a, b), -Infinity);
    return [
      {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [(minLng + maxLng) / 2, (minLat + maxLat) / 2],
        },
        properties: {
          id: p.id,
          name: p.name,
          icon: `${p.land_type}-icon`,
        },
      },
    ];
  });
}

/** Merges the (cheap) selected flag into precomputed centroid features. */
function withSelectedFlag(
  features: ReturnType<typeof buildCentroidFeatures>,
  selectedIds: Set<string>,
): FeatureCollection<Point> {
  return featureCollection(
    features.map((feature) => ({
      ...feature,
      properties: { ...feature.properties, selected: selectedIds.has(feature.properties.id) },
    })),
  ) as FeatureCollection<Point>;
}

function buildPlotCentroids(plots: DraftPlot[]): Record<string, { lng: number; lat: number }> {
  return plots.reduce<Record<string, { lng: number; lat: number }>>((acc, plot) => {
    if (!plot.multi_polygon) return acc;
    const geometry = plot.multi_polygon as unknown as { coordinates: number[][][][] };
    const coordinates = geometry.coordinates.flat(2);
    const lngs = coordinates.map((c) => Number(c[0])).filter(Number.isFinite);
    const lats = coordinates.map((c) => Number(c[1])).filter(Number.isFinite);
    if (!lngs.length || !lats.length) return acc;
    const minLng = lngs.reduce((a, b) => Math.min(a, b), Infinity);
    const maxLng = lngs.reduce((a, b) => Math.max(a, b), -Infinity);
    const minLat = lats.reduce((a, b) => Math.min(a, b), Infinity);
    const maxLat = lats.reduce((a, b) => Math.max(a, b), -Infinity);
    acc[plot.id] = {
      lng: (minLng + maxLng) / 2,
      lat: (minLat + maxLat) / 2,
    };
    return acc;
  }, {});
}

function fitMapToDraftPlots(mapRef: RefObject<MapRef>, plots: DraftPlot[]) {
  if (!mapRef.current) return;
  const coords = plots.flatMap((p) => {
    if (!p.multi_polygon) return [];
    return (p.multi_polygon as unknown as { coordinates: number[][][][] }).coordinates.flat(2);
  });
  if (!coords.length) return;
  const lngs = coords.map((c) => c[0] as number);
  const lats = coords.map((c) => c[1] as number);
  const minLng = lngs.reduce((a, b) => Math.min(a, b), Infinity);
  const maxLng = lngs.reduce((a, b) => Math.max(a, b), -Infinity);
  const minLat = lats.reduce((a, b) => Math.min(a, b), Infinity);
  const maxLat = lats.reduce((a, b) => Math.max(a, b), -Infinity);
  mapRef.current.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    { padding: 40, duration: 800 },
  );
}

function useResetSelectionOnOrganizationChange(
  organizationId: string | null,
  setRowSelection: (selection: RowSelectionState) => void,
) {
  useEffect(() => {
    setRowSelection({});
  }, [organizationId, setRowSelection]);
}

function useFitMapToAllDraftPlots(
  mapLoaded: boolean,
  draftPlotsGeoJSON: ReturnType<typeof buildDraftPlotsGeoJSON>,
  mapRef: RefObject<MapRef>,
  allDraftPlots: DraftPlot[],
) {
  useEffect(() => {
    if (!mapLoaded || !draftPlotsGeoJSON) return;
    fitMapToDraftPlots(mapRef, allDraftPlots);
  }, [mapLoaded, draftPlotsGeoJSON, allDraftPlots, mapRef]);
}

function useFitMapToSelectedDraftPlots(
  mapLoaded: boolean,
  mapRef: RefObject<MapRef>,
  allDraftPlots: DraftPlot[],
  selectedPlotIds: Set<string>,
) {
  useEffect(() => {
    if (!mapLoaded || !selectedPlotIds.size) return;
    const selectedPlots = allDraftPlots.filter((plot) => selectedPlotIds.has(plot.id));
    if (!selectedPlots.length) return;
    fitMapToDraftPlots(mapRef, selectedPlots);
  }, [mapLoaded, mapRef, allDraftPlots, selectedPlotIds]);
}

function useFocusDraftPlotOnMap(
  mapLoaded: boolean,
  mapRef: RefObject<MapRef>,
  allDraftPlots: DraftPlot[],
  focusedPlotId?: string | null,
) {
  useEffect(() => {
    if (!mapLoaded || !focusedPlotId) return;
    const focusedPlot = allDraftPlots.find((plot) => plot.id === focusedPlotId);
    if (!focusedPlot) return;
    fitMapToDraftPlots(mapRef, [focusedPlot]);
  }, [mapLoaded, mapRef, allDraftPlots, focusedPlotId]);
}

type UseDraftPlotsSearchParams = {
  mapRef: RefObject<MapRef>;
  mapLoaded: boolean;
  // Staff-only: widens results to all drafts for the organization, not just the requester's own uploads
  allOrgDrafts?: boolean;
  // Plot selected for edit mode; when set, map should focus that plot.
  focusedPlotId?: string | null;
};

/** Fetches draft plots, filters by search term, builds GeoJSON layers, and tracks hovered plot. */
export const useDraftPlotsSearch = ({ mapRef, mapLoaded, allOrgDrafts, focusedPlotId }: UseDraftPlotsSearchParams) => {
  const organizationId = useOrganizationId();
  const [searchTerm, setSearchTerm] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredPlotId, setHoveredPlotId] = useState<string | null>(null);

  const { data: draftPlotsData, refetch: refetchDraftPlots } = useGetDraftPlot(
    { queryParams: { organization: organizationId || undefined, ...(allOrgDrafts ? { all_org_drafts: true } : {}) } },
    { enabled: !!organizationId },
  );
  const allDraftPlots = useMemo(() => draftPlotsData?.results ?? [], [draftPlotsData?.results]);

  const filteredPlots = useMemo(
    () =>
      searchTerm ? allDraftPlots.filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase())) : allDraftPlots,
    [allDraftPlots, searchTerm],
  );

  const selectedPlotIds = useMemo(
    () =>
      new Set(
        Object.entries(rowSelection)
          .filter(([, selected]) => selected)
          .map(([id]) => id),
      ),
    [rowSelection],
  );

  const draftPlotsGeoJSON = useMemo(() => buildDraftPlotsGeoJSON(allDraftPlots), [allDraftPlots]);
  const plotCentroids = useMemo(() => buildPlotCentroids(allDraftPlots), [allDraftPlots]);
  const centroidFeatures = useMemo(() => buildCentroidFeatures(allDraftPlots), [allDraftPlots]);
  const draftPlotCentroidsGeoJSON = useMemo(
    () => withSelectedFlag(centroidFeatures, selectedPlotIds),
    [centroidFeatures, selectedPlotIds],
  );

  useResetSelectionOnOrganizationChange(organizationId, setRowSelection);
  useFitMapToAllDraftPlots(mapLoaded, draftPlotsGeoJSON, mapRef, allDraftPlots);
  useFitMapToSelectedDraftPlots(mapLoaded, mapRef, allDraftPlots, selectedPlotIds);
  useFocusDraftPlotOnMap(mapLoaded, mapRef, allDraftPlots, focusedPlotId);

  const hoveredPlot = allDraftPlots.find((p) => p.id === hoveredPlotId);
  const hoveredCentroid = hoveredPlot ? plotCentroids[hoveredPlot.id] : undefined;

  return {
    organizationId,
    allDraftPlots,
    filteredPlots,
    refetchDraftPlots,
    draftPlotsGeoJSON,
    draftPlotCentroidsGeoJSON,
    hoveredPlotId,
    setHoveredPlotId,
    hoveredPlot,
    hoveredCentroid,
    rowSelection,
    setRowSelection,
    selectedPlotIds,
    sorting,
    setSorting,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
  };
};
